/**
 * src/features/admin/ui/ActivationRateGrid.tsx
 *
 * Activation-rate cohort cards — 8 ISO-week cohort stat cards (Phase 23 / METRIC-02).
 * Mirrors CountGrid layout: grid-cols-2 md:grid-cols-4 gap-4 md:gap-6.
 * Rate colored by threshold: ≥35% green, <25% amber, 25–34% neutral.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';
import type { AdminMetrics } from '@/shared/types/admin';

// ---------------------------------------------------------------------------
// File-local helpers
// ---------------------------------------------------------------------------

function rateColor(rate: number): string {
  if (rate >= 0.35) return 'text-green-600 dark:text-green-400';
  if (rate < 0.25) return 'text-amber-600 dark:text-amber-400';
  return 'text-slate-900 dark:text-slate-50';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ActivationRateGridProps {
  cohorts: NonNullable<AdminMetrics['activation']>['cohorts'];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActivationRateGrid({
  cohorts,
}: ActivationRateGridProps): React.ReactElement {
  const { t } = useTranslation();

  const isEmpty =
    cohorts.length === 0 || cohorts.every((c) => c.started === 0);

  if (isEmpty) {
    return (
      <div className="py-10 text-center">
        <p className="text-body text-slate-400 dark:text-slate-500">
          {t('admin.activation.no_data', 'No cohort data yet — waiting for first exports.')}
        </p>
      </div>
    );
  }

  return (
    <>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {cohorts.map((cohort) => {
          const ratePercent = (cohort.activationRate * 100).toFixed(1);
          return (
            <Card key={cohort.week}>
              <CardContent className="p-6 flex flex-col gap-2">
                <dt className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
                  {cohort.week}
                </dt>
                <dd
                  className={`text-subhead font-bold font-mono tabular-nums ${rateColor(cohort.activationRate)}`}
                >
                  {ratePercent}%
                </dd>
                <p className="text-caption text-slate-400 dark:text-slate-500">
                  {t('admin.activation.activated_of', '{{activated}} / {{started}} activated', {
                    activated: cohort.activated,
                    started: cohort.started,
                  })}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </dl>
      <p className="text-caption text-slate-400 dark:text-slate-500 mt-2">
        {t('admin.activation.target_hint', 'Target: ≥25% healthy · ≥35% good')}
      </p>
    </>
  );
}
